'use client';

import { IActiveNavConfig, IBlogConfig } from '@/api/types/blog';
import Accordion, { AccordionItemProps } from '@/components/Accordion';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import { BLOG_MARK_LIST } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Arrow_down, CloseOutlined, MinusOutlined, PlusOutlined } from '@zeelool/icon';
import Link from 'next/link';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface BlogNavProps {
  activeNav?: IActiveNavConfig;
  navList?: IBlogConfig[];
  isType?: boolean;
  onTypeChange?: (nav: any) => void;
  onClose?: () => void;
  className?: string;
  title?: string;
}

interface NavItem extends IBlogConfig {
  show?: boolean;
}

const BlogNav: React.FC<BlogNavProps> = ({
  activeNav = {},
  navList = [],
  isType = false,
  onTypeChange = () => {},
  onClose = () => {},
  className = '',
  title = '',
}) => {
  const [nav, setNav] = useState<NavItem[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const { deviceType } = useContext(ResponsiveContext);

  // 处理navList变化
  useEffect(() => {
    if (Array.isArray(navList) && navList.length) {
      // 先筛选出普通导航项并排序
      const commonNavList = navList
        .filter((item) => !['all_blogs', 'shop_zeelool'].includes(item.mark))
        .sort((a, b) => a.sort - b.sort);

      // 处理普通导航项
      const processedNav = commonNavList.map((item) => ({ ...item, show: true }));

      // 获取特殊导航项
      const allBlogsNav = navList.find((item) => item.mark === 'all_blogs');
      const shopZeeloolNav = navList.find((item) => item.mark === 'shop_zeelool');

      // 合并所有导航项
      const finalNav = [...processedNav];
      if (allBlogsNav) finalNav.push({ ...allBlogsNav, show: false });
      if (shopZeeloolNav) finalNav.push({ ...shopZeeloolNav, show: false });

      setNav(finalNav);

      // 默认展开所有有子项的导航
      const defaultExpanded = finalNav
        .filter((item) => item.content?.child && item.content.child.length > 0)
        .map((item) => item.id?.toString() || '');
      setExpandedKeys(defaultExpanded);
    }
  }, [navList]);

  // 判断是否为数组
  const isArray = (value: any): value is any[] => Array.isArray(value);

  // 处理子导航点击
  const handleChildClick = useCallback(
    (child: any, parentName: string) => {
      onTypeChange({ ...child, parent_name: parentName });
    },
    [onTypeChange],
  );

  // 判断一级导航是否激活
  const isLevelOneActive = useCallback(
    (item: NavItem) => {
      return (
        item.mark === activeNav.child_id && (!item.content?.child || !item.content.child.length)
      );
    },
    [activeNav.child_id],
  );

  // 处理简单导航项点击（没有子项的）
  const handleSimpleNavClick = useCallback(
    (item: NavItem) => {
      if ([BLOG_MARK_LIST.ALL_BLOGS, BLOG_MARK_LIST.SHOP_ZEELOOL].includes(item.mark)) {
        onTypeChange(item);
      }
    },
    [onTypeChange],
  );

  // 转换为Accordion所需的数据格式
  const accordionItems: AccordionItemProps[] = useMemo(() => {
    return nav
      .filter((item) => isArray(item.content?.child) && item.content.child.length > 0)
      .map((item) => ({
        key: item.id?.toString() || '',
        title: (
          <div className="flex items-center">
            <h4
              className={`
                filter-title-text font-bb block
                ${isLevelOneActive(item) ? 'active underline ml-8 bg-[rgba(199,255,87,0.5)]' : ''}
                ${deviceType === EPlatform.WEB ? 'text-16' : 'text-14'}
              `}
            >
              {item.content?.name || ''}
            </h4>
            {isLevelOneActive(item) && (
              <Arrow_down className="active-icon ml-[12px] text-14 rotate-270" />
            )}
          </div>
        ),
        content: (
          <ul className={`filter-child-list ${deviceType === EPlatform.WEB ? 'pt-20' : ''}`}>
            {item.content?.child?.map((child: any) => (
              <li
                key={child.child_id}
                className={`filter-child-item flex items-center ${deviceType === EPlatform.WEB ? 'mb-24 last:mb-0' : 'mt-16'}`}
              >
                <Link
                  prefetch={false}
                  className={`
                    filter-child-text block cursor-pointer
                    ${child.child_id === activeNav.child_id ? 'active underline ml-8 font-bb bg-[rgba(199,255,87,0.5)]' : ''}
                    ${deviceType === EPlatform.WEB ? 'text-14 hover:underline' : 'text-13'}
                  `}
                  onClick={(e) => {
                    e.preventDefault();
                    handleChildClick(child, item.content?.name || '');
                  }}
                  href={`/blog/${child.another_name}`}
                >
                  {child.name}
                </Link>
                {child.child_id === activeNav.child_id && (
                  <Arrow_down className="active-icon ml-[11px] text-12 rotate-270" />
                )}
              </li>
            ))}
          </ul>
        ),
        // 使用自定义图标函数
        expandIcon: (expanded: boolean) => (expanded ? <MinusOutlined /> : <PlusOutlined />),
      }));
  }, [nav, activeNav.child_id, isLevelOneActive, handleChildClick]);

  // 处理Accordion变化
  const handleAccordionChange = useCallback((keys: string | string[]) => {
    setExpandedKeys(Array.isArray(keys) ? keys : [keys]);
  }, []);

  return (
    <div
      className={`
        blog-nav
        ${className}
        ${deviceType === EPlatform.WEB ? 'pr-24 pb-[96px] pl-48 w-full' : 'bg-white w-[270px]'}
      `}
    >
      {title && <h3 className="text-32 font-bb pt-40 pb-[8px]">{title}</h3>}
      <WapOnly>
        {/* 头部关闭按钮 */}
        <div className="pop-header px-14 pr-16 pt-24 pb-8 bg-white sticky top-0 w-full h-24 box-border flex justify-end items-center z-10">
          <CloseOutlined className="text-24 cursor-pointer" onClick={onClose} />
        </div>
      </WapOnly>

      {/* 导航列表 */}
      <div className={`${deviceType !== EPlatform.WEB ? 'px-14 pr-16' : ''}`}>
        {nav.map((item) => {
          // 如果是有子项的导航，使用Accordion渲染
          if (isArray(item.content?.child) && item.content.child.length > 0) {
            const accordionItem = accordionItems.find(
              (accItem) => accItem.key === item.id?.toString(),
            );
            if (!accordionItem) return null;

            return (
              <div key={item.id} className="filter-item">
                <Accordion
                  items={[accordionItem]}
                  activeKey={expandedKeys}
                  onChange={handleAccordionChange}
                  accordion={false}
                  sx={{
                    '&.MuiAccordion-root': {
                      borderRadius: '0',
                      boxShadow: 'none',
                      mb: 0,
                      backgroundColor: deviceType === EPlatform.WEB ? 'transparent' : 'white',
                      '&:before': {
                        display: 'none',
                      },
                    },
                    '& .MuiAccordionSummary-content': {
                      margin: '0',
                      '&.Mui-expanded': {
                        margin: '0',
                      },
                    },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                      color: '#282828',
                      '& svg': {
                        fontSize: '18px',
                      },
                    },
                  }}
                  summaryStyle={{
                    padding: '28px 0 6px 0',
                    minHeight: 'auto !important',
                    borderBottom: '1px solid #808080',
                  }}
                  detailsStyle={{
                    padding: 0,
                  }}
                />
              </div>
            );
          } else {
            // 简单导航项（没有子项的）
            return (
              <div key={item.id} className="filter-item">
                <div
                  className={`
                    filter-title flex justify-between items-center border-b border-[#808080] cursor-pointer
                    ${deviceType === EPlatform.WEB ? 'pt-32 pb-12' : 'pt-28 pb-6'}
                  `}
                  onClick={() => handleSimpleNavClick(item)}
                >
                  <div className="filter-title-text-box flex items-center">
                    {!isType ? (
                      <h4
                        className={`
                          filter-title-text font-bb block
                          ${isLevelOneActive(item) ? 'active underline ml-8 bg-[rgba(199,255,87,0.5)]' : ''}
                          ${deviceType === EPlatform.WEB ? 'text-16 hover:underline' : 'text-14'}
                        `}
                      >
                        {item.content?.name}
                      </h4>
                    ) : (
                      <>
                        {['all_blogs', 'shop_zeelool'].includes(item.mark) ? (
                          <Link
                            prefetch={false}
                            href="/blog"
                            className={`filter-title-text font-bb block ${
                              isLevelOneActive(item)
                                ? 'active underline ml-8 bg-[rgba(199,255,87,0.5)]'
                                : ''
                            } ${deviceType === EPlatform.WEB ? 'text-14 hover:underline' : 'text-13'}`}
                            aria-label={item.content?.name}
                          >
                            {item.content?.name}
                          </Link>
                        ) : (
                          <h4
                            className={`filter-title-text font-bb block ${
                              isLevelOneActive(item)
                                ? 'active underline ml-8 bg-[rgba(199,255,87,0.5)]'
                                : ''
                            } ${deviceType === EPlatform.WEB ? 'text-14 hover:underline' : 'text-13'}`}
                          >
                            {item.content?.name}
                          </h4>
                        )}
                      </>
                    )}
                    {isLevelOneActive(item) && (
                      <Arrow_down className="active-icon ml-[12px] text-14 rotate-270" />
                    )}
                  </div>
                </div>
              </div>
            );
          }
        })}
        <WapOnly>
          <div className="h-60" />
        </WapOnly>
      </div>
    </div>
  );
};

export default BlogNav;
